import React from 'react';
import { 
  Box, 
  Skeleton, 
  CircularProgress, 
  LinearProgress, 
  Typography,
  Fade 
} from '@mui/material';

// Page-level skeleton loader
export const PageSkeleton = () => (
  <Box sx={{ p: 3 }}>
    {/* Hero skeleton */}
    <Skeleton 
      variant="rectangular" 
      height={400} 
      sx={{ mb: 4, borderRadius: 2 }} 
      animation="wave"
    />
    
    {/* Content blocks */}
    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
      {[1, 2, 3].map((item) => (
        <Box key={item}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" height={30} width="80%" sx={{ mb: 1 }} />
          <Skeleton variant="text" height={20} width="60%" />
        </Box>
      ))}
    </Box>
  </Box>
);

// Form field skeleton
export const FormSkeleton = ({ fields = 6 }) => (
  <Box sx={{ display: 'grid', gap: 3 }}>
    {Array.from({ length: fields }).map((_, index) => (
      <Box key={index}>
        <Skeleton variant="text" height={20} width="30%" sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Box>
);

// Advanced progress indicator - Mobile responsive
export const ProgressStepper = ({ 
  activeStep, 
  steps, 
  completedSteps = [],
  sx = {},
  onStepClick
}) => (
  <>
    {/* Mobile Progress Indicator - Compact view */}
    <Box sx={{ 
      display: { xs: 'block', md: 'none' }, 
      mb: 3,
      ...sx 
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Step {activeStep + 1} of {steps.length}
        </Typography>
        <Typography variant="body2" color="primary.main" fontWeight="bold">
          {Math.round(((activeStep + 1) / steps.length) * 100)}%
        </Typography>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            width: '100%',
            height: 6,
            backgroundColor: 'grey.200',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              width: `${((activeStep + 1) / steps.length) * 100}%`,
              height: '100%',
              backgroundColor: 'primary.main',
              transition: 'width 0.5s ease-in-out',
              borderRadius: 3
            }}
          />
        </Box>
      </Box>
      
      <Typography 
        variant="h2" 
        component="h2" 
        sx={{ 
          fontWeight: 600,
          color: 'primary.main',
          textAlign: 'center',
          fontSize: '1.25rem'
        }}
      >
        {steps[activeStep]}
      </Typography>
    </Box>

    {/* Desktop Progress Stepper - Full view */}
    <Box sx={{ 
      display: { xs: 'none', md: 'flex' }, 
      alignItems: 'center', 
      mb: 4, 
      ...sx 
    }}>
      {steps.map((step, index) => (
        <React.Fragment key={step}>
          <Box 
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 
                  completedSteps.includes(index) ? 'success.main' :
                  index === activeStep ? 'primary.main' : 'grey.300',
                color: 
                  completedSteps.includes(index) || index === activeStep ? 'white' : 'grey.600',
                transition: 'all 0.3s ease',
                transform: index === activeStep ? 'scale(1.1)' : 'scale(1)',
                boxShadow: index === activeStep ? 2 : 0,
                cursor: onStepClick && completedSteps.includes(index) ? 'pointer' : 'default',
                '&:hover': onStepClick && completedSteps.includes(index) ? {
                  filter: 'brightness(1.05)'
                } : {}
              }}
              role={onStepClick && completedSteps.includes(index) ? 'button' : undefined}
              tabIndex={onStepClick && completedSteps.includes(index) ? 0 : -1}
              onClick={() => {
                if (onStepClick && completedSteps.includes(index)) onStepClick(index);
              }}
              onKeyDown={(e) => {
                if (!onStepClick || !completedSteps.includes(index)) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick(index);
                }
              }}
            >
              {completedSteps.includes(index) ? '✓' : index + 1}
            </Box>
            <Typography 
              variant="caption" 
              sx={{ 
                mt: 1, 
                fontWeight: index === activeStep ? 'bold' : 'normal',
                color: index === activeStep ? 'primary.main' : 'text.secondary',
                textAlign: 'center',
                maxWidth: 80,
                cursor: onStepClick && completedSteps.includes(index) ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (onStepClick && completedSteps.includes(index)) onStepClick(index);
              }}
              onKeyDown={(e) => {
                if (!onStepClick || !completedSteps.includes(index)) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick(index);
                }
              }}
            >
              {step}
            </Typography>
          </Box>
          
          {index < steps.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: 2,
                mx: 2,
                backgroundColor: completedSteps.includes(index) ? 'success.main' : 'grey.300',
                transition: 'background-color 0.3s ease'
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  </>
);

// Loading overlay
export const LoadingOverlay = ({ 
  isLoading, 
  message = 'Loading...', 
  children,
  backdrop = true 
}) => (
  <Box sx={{ position: 'relative' }}>
    {children}
    
    <Fade in={isLoading}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: backdrop ? 'rgba(255, 255, 255, 0.8)' : 'transparent',
          backdropFilter: backdrop ? 'blur(2px)' : 'none',
          zIndex: 1000
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
    </Fade>
  </Box>
);
